import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Truck, Clock, Sparkles, Package, MapPin, Shield, 
  CheckCircle, ArrowRight, Menu, X, Star, Handshake
} from 'lucide-react';
import { PartnershipDialog } from '../components/landing/PartnershipDialog';
import { HeroSplit } from '../components/landing/HeroSplit';
import { SustainabilitySection } from '../components/landing/SustainabilitySection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [clientForm, setClientForm] = useState({ name: '', email: '', phone: '', business: '' });
  const [courierForm, setCourierForm] = useState({ name: '', email: '', phone: '', vehicle: '' });
  const [clientSubmitted, setClientSubmitted] = useState(false);
  const [courierSubmitted, setCourierSubmitted] = useState(false);
  const [partnershipOpen, setPartnershipOpen] = useState(false);

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/leads`, {
        name: clientForm.name,
        email: clientForm.email,
        phone: clientForm.phone,
        business: clientForm.business,
        lead_type: 'customer'
      });
      
      // console.log('Cliente interesse registado:', response.data); // Removed for production
      setClientSubmitted(true);
      
      // Redirect to register after 2 seconds
      setTimeout(() => navigate('/register?type=customer'), 2000);
    } catch (error) {
      // console.error('Erro ao registar interesse:', error); // Removed for production
      alert('Erro ao registar interesse. Por favor, tente novamente.');
    }
  };

  const handleCourierSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/leads`, {
        name: courierForm.name,
        email: courierForm.email,
        phone: courierForm.phone,
        vehicle: courierForm.vehicle,
        lead_type: 'driver'
      });
      
      // console.log('Estafeta interesse registado:', response.data); // Removed for production
      setCourierSubmitted(true);
      
      // Redirect to register after 2 seconds
      setTimeout(() => navigate('/register?type=driver'), 2000);
    } catch (error) {
      // console.error('Erro ao registar interesse:', error); // Removed for production
      alert('Erro ao registar interesse. Por favor, tente novamente.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-md bg-white/70 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#10b981] rounded-xl flex items-center justify-center">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-900">TugGo</span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#servicos" className="text-slate-600 hover:text-slate-900 font-medium transition-colors" data-testid="nav-services">
                Serviços
              </a>
              <a href="#clientes" className="text-slate-600 hover:text-slate-900 font-medium transition-colors" data-testid="nav-clients">
                Para Clientes
              </a>
              <a href="#estafetas" className="text-slate-600 hover:text-slate-900 font-medium transition-colors" data-testid="nav-couriers">
                Para Estafetas
              </a>
              <a href="#parcerias" className="text-slate-600 hover:text-slate-900 font-medium transition-colors" data-testid="nav-partnerships">
                Parcerias
              </a>
            </nav>

            {/* CTAs */}
            <div className="hidden md:flex items-center gap-4">
              <button
                onClick={() => setPartnershipOpen(true)}
                className="flex items-center gap-2 border border-[#10b981] text-[#059669] hover:bg-[#10b981]/10 px-4 py-2.5 rounded-xl font-medium transition-colors"
                data-testid="header-partnership-btn"
              >
                <Handshake className="w-4 h-4" /> Seja Parceiro
              </button>
              <Link 
                to="/login" 
                className="text-slate-600 hover:text-slate-900 px-4 py-2 rounded-xl font-medium transition-all"
                data-testid="header-login"
              >
                Entrar
              </Link>
              <Link 
                to="/register" 
                className="bg-[#10b981] text-white hover:bg-[#059669] px-6 py-3 rounded-xl font-medium transition-all shadow-sm hover:shadow-md"
                data-testid="header-register"
              >
                Começar Agora
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-100">
              <div className="flex flex-col gap-4">
                <a href="#servicos" className="text-slate-600 hover:text-slate-900 py-2">Serviços</a>
                <a href="#clientes" className="text-slate-600 hover:text-slate-900 py-2">Para Clientes</a>
                <a href="#estafetas" className="text-slate-600 hover:text-slate-900 py-2">Para Estafetas</a>
                <button
                  onClick={() => { setMobileMenuOpen(false); setPartnershipOpen(true); }}
                  className="flex items-center gap-2 text-[#059669] font-medium py-2 text-left"
                  data-testid="mobile-partnership-btn"
                >
                  <Handshake className="w-4 h-4" /> Seja Parceiro
                </button>
                <Link to="/login" className="text-slate-600 hover:text-slate-900 py-2">Entrar</Link>
                <Link to="/register" className="bg-[#10b981] text-white px-6 py-3 rounded-xl text-center">
                  Começar Agora
                </Link>
              </div>
            </div>
          )}
        </div>
      </header>

      <HeroSplit />

      <SustainabilitySection />

      {/* Services Section */}
      <section id="servicos" className="py-24 px-6 md:px-12 lg:px-24 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#10b981] mb-4">SERVIÇOS</div>
            <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-4">
              Soluções completas para todas as suas necessidades
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Service 1 */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-[#d1fae5] rounded-2xl flex items-center justify-center mb-6">
                <Truck className="w-7 h-7 text-[#10b981]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Entregas Rápidas</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Mota, carro, carrinhas furgão e carrinhas contentor. Escolha o veículo ideal para o seu envio.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-[#10b981] mr-2" />
                  Rastreamento em tempo real
                </li>
                <li className="flex items-center text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-[#10b981] mr-2" />
                  Múltiplos veículos disponíveis
                </li>
              </ul>
            </div>

            {/* Service 2 */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-[#d1fae5] rounded-2xl flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-[#10b981]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Estafeta à Hora</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Contrate um estafeta dedicado por horas. Perfeito para restaurantes e empresas.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-[#10b981] mr-2" />
                  1 a 8 horas de serviço
                </li>
                <li className="flex items-center text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-[#10b981] mr-2" />
                  Ideal para múltiplas entregas
                </li>
              </ul>
            </div>

            {/* Service 3 */}
            <div className="bg-gradient-to-br from-[#10b981] to-[#059669] rounded-2xl p-8 text-white hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-3">IA de Medição</h3>
              <p className="text-white/90 leading-relaxed mb-4">
                Tire uma foto do objeto e nossa IA recomenda o veículo ideal e calcula o preço.
              </p>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/0ca1b2c0-495a-4032-aa3f-8b74f40e0c75/images/08966cabce7f3fa206270270b337243343f9c2b39b87ba82658cc4fc0586000c.png"
                alt="IA de Medição"
                className="rounded-xl mt-4 w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Para Clientes Section */}
      <section id="clientes" className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Left: Image */}
            <div>
              <img 
                src="https://images.unsplash.com/photo-1753351051161-21de8728ec17?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGNhZmUlMjBvd25lciUyMGJ1c2luZXNzfGVufDB8fHx8MTc3NjQzNzMxM3ww&ixlib=rb-4.1.0&q=85"
                alt="Cliente satisfeito"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>

            {/* Right: Content + Form */}
            <div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#10b981] mb-4">PARA CLIENTES</div>
              <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 mb-6">
                Envie com segurança e rapidez
              </h2>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <Shield className="w-6 h-6 text-[#10b981] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Verificação KYC Segura</h4>
                    <p className="text-slate-600 text-sm">Todos os estafetas são verificados para sua segurança</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Package className="w-6 h-6 text-[#10b981] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Pagamento Seguro com PayPal</h4>
                    <p className="text-slate-600 text-sm">Pague com segurança através do PayPal</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-[#10b981] flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Rastreamento em Tempo Real</h4>
                    <p className="text-slate-600 text-sm">Acompanhe sua entrega do início ao fim</p>
                  </div>
                </div>
              </div>

              {/* Client Interest Form */}
              <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
                {clientSubmitted ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 text-[#10b981] mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Obrigado!</h3>
                    <p className="text-slate-600">A redirecionar para o registo...</p>
                  </div>
                ) : (
                  <form onSubmit={handleClientSubmit} data-testid="client-form">
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Registe o seu Interesse</h3>
                    <p className="text-sm text-slate-600 mb-4">Quer ser notificado quando lançarmos? Preencha o formulário abaixo.</p>
                    <div className="space-y-4">
                      <input
                        type="text"
                        placeholder="Nome completo"
                        value={clientForm.name}
                        onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981]"
                        required
                        data-testid="client-name"
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={clientForm.email}
                        onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981]"
                        required
                        data-testid="client-email"
                      />
                      <input
                        type="tel"
                        placeholder="Telefone"
                        value={clientForm.phone}
                        onChange={(e) => setClientForm({...clientForm, phone: e.target.value})}
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981]"
                        required
                        data-testid="client-phone"
                      />
                      <input
                        type="text"
                        placeholder="Tipo de negócio (opcional)"
                        value={clientForm.business}
                        onChange={(e) => setClientForm({...clientForm, business: e.target.value})}
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981]"
                        data-testid="client-business"
                      />
                      <button 
                        type="submit"
                        className="w-full bg-[#10b981] text-white hover:bg-[#059669] px-6 py-3 rounded-xl font-medium transition-all shadow-sm hover:shadow-md"
                        data-testid="client-submit"
                      >
                        Começar Agora
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Para Estafetas Section */}
      <section id="estafetas" className="py-24 px-6 md:px-12 lg:px-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Left: Form */}
            <div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#10b981] mb-4">PARA ESTAFETAS</div>
              <h2 className="text-3xl sm:text-4xl font-semibold mb-6">
                Ganhe dinheiro com flexibilidade
              </h2>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <CheckCircle className="w-6 h-6 text-[#10b981] flex-shrink-0" />
                  <p className="text-slate-300">Escolha seus próprios horários</p>
                </div>
                <div className="flex items-center gap-4">
                  <CheckCircle className="w-6 h-6 text-[#10b981] flex-shrink-0" />
                  <p className="text-slate-300">Receba pagamentos semanais</p>
                </div>
                <div className="flex items-center gap-4">
                  <CheckCircle className="w-6 h-6 text-[#10b981] flex-shrink-0" />
                  <p className="text-slate-300">Comandos de voz por IA</p>
                </div>
                <div className="flex items-center gap-4">
                  <CheckCircle className="w-6 h-6 text-[#10b981] flex-shrink-0" />
                  <p className="text-slate-300">Suporte 24/7</p>
                </div>
              </div>

              {/* Courier Interest Form */}
              <div className="bg-slate-800 rounded-2xl p-8 border border-slate-700">
                {courierSubmitted ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 text-[#10b981] mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Obrigado!</h3>
                    <p className="text-slate-300">A redirecionar para o registo...</p>
                  </div>
                ) : (
                  <form onSubmit={handleCourierSubmit} data-testid="courier-form">
                    <h3 className="text-xl font-semibold mb-2">Junte-se à TugGo</h3>
                    <p className="text-sm text-slate-300 mb-4">Interessado em ser estafeta? Registe-se para ser notificado no lançamento!</p>
                    <div className="space-y-4">
                      <input
                        type="text"
                        placeholder="Nome completo"
                        value={courierForm.name}
                        onChange={(e) => setCourierForm({...courierForm, name: e.target.value})}
                        className="w-full rounded-xl border border-slate-600 bg-slate-700 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] text-white placeholder:text-slate-400"
                        required
                        data-testid="courier-name"
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={courierForm.email}
                        onChange={(e) => setCourierForm({...courierForm, email: e.target.value})}
                        className="w-full rounded-xl border border-slate-600 bg-slate-700 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] text-white placeholder:text-slate-400"
                        required
                        data-testid="courier-email"
                      />
                      <input
                        type="tel"
                        placeholder="Telefone"
                        value={courierForm.phone}
                        onChange={(e) => setCourierForm({...courierForm, phone: e.target.value})}
                        className="w-full rounded-xl border border-slate-600 bg-slate-700 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] text-white placeholder:text-slate-400"
                        required
                        data-testid="courier-phone"
                      />
                      <select
                        value={courierForm.vehicle}
                        onChange={(e) => setCourierForm({...courierForm, vehicle: e.target.value})}
                        className="w-full rounded-xl border border-slate-600 bg-slate-700 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#10b981]/20 focus:border-[#10b981] text-white"
                        required
                        data-testid="courier-vehicle"
                      >
                        <option value="">Tipo de veículo</option>
                        <option value="mota">Mota</option>
                        <option value="carro">Carro</option>
                        <option value="carrinha">Carrinha</option>
                        <option value="camiao">Camião</option>
                      </select>
                      <button 
                        type="submit"
                        className="w-full bg-[#10b981] text-white hover:bg-[#059669] px-6 py-3 rounded-xl font-medium transition-all shadow-sm hover:shadow-md"
                        data-testid="courier-submit"
                      >
                        Juntar-me à TugGo
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>

            {/* Right: Image */}
            <div>
              <img 
                src="https://images.unsplash.com/photo-1551825687-f9de1603ed8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxkZWxpdmVyeSUyMGRyaXZlciUyMHNtaWxpbmclMjBob2xkaW5nJTIwcGFja2FnZXxlbnwwfHx8fDE3NzY0MzczMjV8MA&ixlib=rb-4.1.0&q=85"
                alt="Estafeta feliz"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Como Funciona */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#10b981] mb-4">COMO FUNCIONA</div>
            <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 mb-4">
              Simples, rápido e seguro
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-[#10b981] rounded-full flex items-center justify-center text-white text-3xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Verificação KYC</h3>
              <p className="text-slate-600">
                Registe-se e complete a verificação de identidade para garantir segurança
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-[#10b981] rounded-full flex items-center justify-center text-white text-3xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Criar Pedido</h3>
              <p className="text-slate-600">
                Use nossa IA para analisar o objeto ou escolha manualmente o veículo
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-[#10b981] rounded-full flex items-center justify-center text-white text-3xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Entrega Rápida</h3>
              <p className="text-slate-600">
                Acompanhe em tempo real e receba sua encomenda com segurança
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Parcerias */}
      <section id="parcerias" className="py-24 px-6 md:px-12 lg:px-24 bg-[#10b981]/5" data-testid="partnership-section">
        <div className="max-w-7xl mx-auto grid md:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#10b981] mb-4">PARCERIAS</div>
            <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 mb-4">
              A sua empresa quer entregar mais rápido?
            </h2>
            <p className="text-slate-600 text-base md:text-lg max-w-xl">
              Lojas, restaurantes, e-commerce e empresas de logística: trabalhe connosco com condições empresariais, faturação mensal e estafetas verificados em todo o país.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
            <Handshake className="w-10 h-10 text-[#10b981] mb-4" />
            <p className="text-slate-700 mb-6">Envie-nos um pedido e a equipa TugGo entra em contacto consigo.</p>
            <button
              onClick={() => setPartnershipOpen(true)}
              className="w-full bg-[#10b981] hover:bg-[#059669] text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
              data-testid="partnership-cta-btn"
            >
              Quero ser parceiro <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      <PartnershipDialog open={partnershipOpen} onOpenChange={setPartnershipOpen} />

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Logo */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-[#10b981] rounded-xl flex items-center justify-center">
                  <Truck className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-white">TugGo</span>
              </div>
              <p className="text-sm">
                A revolução nas entregas em Portugal
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Empresa</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-[#10b981] transition-colors">Sobre Nós</a></li>
                <li>
                  <button onClick={() => setPartnershipOpen(true)} className="hover:text-[#10b981] transition-colors" data-testid="footer-partnership-btn">
                    Parcerias
                  </button>
                </li>
                <li><a href="#" className="hover:text-[#10b981] transition-colors">Carreiras</a></li>
                <li><a href="#" className="hover:text-[#10b981] transition-colors">Blog</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Suporte</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-[#10b981] transition-colors">Central de Ajuda</a></li>
                <li>
                  <a 
                    href="mailto:duvidastuggo@gmail.com?subject=Interesse%20TugGo&body=Olá%2C%20tenho%20interesse%20em%20saber%20mais%20sobre%20a%20TugGo." 
                    className="hover:text-[#10b981] transition-colors"
                  >
                    📧 Contactar-nos
                  </a>
                </li>
                <li><a href="#" className="hover:text-[#10b981] transition-colors">FAQ</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-[#10b981] transition-colors">Termos de Uso</a></li>
                <li><a href="#" className="hover:text-[#10b981] transition-colors">Privacidade</a></li>
                <li><a href="#" className="hover:text-[#10b981] transition-colors">Cookies</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} TugGo Portugal. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
